import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Warning, X, ArrowRight, ArrowSquareOut } from "@phosphor-icons/react";

const DISMISS_KEY = "feeAlertDismissed";

/** Signature of the current mismatch state so a dismissed banner reappears
 *  the moment a fee changes (either the firm's config or the official rate). */
function signatureOf(data) {
  const parts = (data.mismatches || [])
    .map((m) => `${m.key}:${m.configured}->${m.official}`)
    .sort();
  return `${data.effective_date}|${parts.join(",")}`;
}

/**
 * Fee Auto-Update Alert — reconciliation banner. Compares the firm's
 * configured disbursement fees against the official published HMLR / Land
 * Charges rates and warns when a stale (below-official) rate is in use.
 * Rendered on the dashboard and the Quotes/Fee Settings page. Visible to all
 * staff; dismissal is remembered per mismatch-signature.
 */
export default function FeeAlertBanner({ className = "" }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let live = true;
    API.get("/fees/reconciliation")
      .then((r) => {
        if (!live) return;
        setData(r.data);
        if (!r.data.ok) {
          const stored = localStorage.getItem(DISMISS_KEY);
          setDismissed(stored === signatureOf(r.data));
        }
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const handleDismiss = useCallback(() => {
    if (data) localStorage.setItem(DISMISS_KEY, signatureOf(data));
    setDismissed(true);
  }, [data]);

  if (!data || data.ok || dismissed) return null;

  return (
    <div
      className={`rounded-lg border border-amber-300 bg-amber-50 p-4 ${className}`}
      data-testid="fee-alert-banner"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-amber-100">
          <Warning weight="fill" className="h-5 w-5 text-amber-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-sm font-semibold text-amber-900" data-testid="fee-alert-title">
              Some disbursement fees are below the current official rate
            </h3>
            <button
              onClick={handleDismiss}
              className="flex-none rounded p-1 text-amber-500 transition-colors hover:bg-amber-100 hover:text-amber-700"
              aria-label="Dismiss"
              data-testid="fee-alert-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-1 text-xs text-amber-800">
            You may be billing stale rates. Official figures below are current as of{" "}
            {new Date(data.effective_date).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}{" "}
            — {data.authority_note}
          </p>

          <ul className="mt-3 space-y-1.5" data-testid="fee-alert-list">
            {data.mismatches.map((m) => (
              <li
                key={m.key}
                className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
                data-testid={`fee-alert-row-${m.key}`}
              >
                <span className="font-medium text-amber-900">{m.label}</span>
                <span className="text-amber-700">
                  billing{" "}
                  <span className="font-semibold text-red-600">£{m.configured}</span>
                  <ArrowRight className="mx-1 inline h-3 w-3" />
                  official{" "}
                  <span className="font-semibold text-emerald-700">£{m.official}</span>
                </span>
                <a
                  href={m.source}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-0.5 text-amber-600 underline decoration-dotted hover:text-amber-800"
                  title={`${m.authority} — view official fee`}
                >
                  {m.authority}
                  <ArrowSquareOut className="h-3 w-3" />
                </a>
              </li>
            ))}
          </ul>

          <div className="mt-3">
            <Button
              size="sm"
              onClick={() => navigate("/quotes?settings=fees")}
              className="h-8 gap-1.5 bg-amber-600 text-xs text-white hover:bg-amber-700"
              data-testid="fee-alert-review-btn"
            >
              Review fees
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
